import { msalInstance } from '../index'

// Token refresh configuration
const TOKEN_REFRESH_THRESHOLD = 5 * 60 * 1000 // 5 minutes before expiry
const TOKEN_CHECK_INTERVAL = 60 * 1000 // Check every minute

interface TokenInfo {
  accessToken: string
  expiresOn: Date
  scopes: string[]
}

// Get token expiry time from JWT token
const getTokenExpiry = (token: string): Date | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return new Date(payload.exp * 1000)
  } catch (error) {
    console.error('Failed to parse token expiry:', error)
    return null
  }
}

// Check if token needs refresh
const shouldRefreshToken = (token: string): boolean => {
  const expiry = getTokenExpiry(token)
  if (!expiry) return true

  const now = new Date()
  const timeUntilExpiry = expiry.getTime() - now.getTime()

  return timeUntilExpiry <= TOKEN_REFRESH_THRESHOLD
}

// Refresh token proactively
export const refreshTokenIfNeeded = async (): Promise<string | null> => {
  try {
    const currentToken = localStorage.getItem('token')
    if (!currentToken) {
      return await acquireNewToken()
    }

    if (shouldRefreshToken(currentToken)) {
      return await acquireNewToken()
    }

    return currentToken
  } catch (error) {
    console.error('Error in token refresh check:', error)
    return null
  }
}

// Acquire a new token
export const acquireNewToken = async (): Promise<string | null> => {
  try {
    const account = msalInstance.getActiveAccount()
    if (!account) {
      console.error('No active account found')
      return null
    }

    const accessTokenResponse = await msalInstance.acquireTokenSilent({
      scopes: [process.env.REACT_APP_EXPOSE_API as string],
      account
    })

    const accessToken = accessTokenResponse.accessToken
    localStorage.setItem('token', accessToken)

    return accessToken
  } catch (error) {
    console.error('Failed to acquire new token:', error)
    return null
  }
}

// Start proactive token refresh
export const startTokenRefreshMonitor = (): void => {
  const checkAndRefreshToken = async () => {
    await refreshTokenIfNeeded()
  }

  // Check immediately
  checkAndRefreshToken()

  // Set up periodic checks
  setInterval(checkAndRefreshToken, TOKEN_CHECK_INTERVAL)
}

// Get current token info
export const getCurrentTokenInfo = (): TokenInfo | null => {
  const token = localStorage.getItem('token')
  if (!token) return null

  const expiry = getTokenExpiry(token)
  if (!expiry) return null

  return {
    accessToken: token,
    expiresOn: expiry,
    scopes: [] // You can extract scopes from token if needed
  }
}

// Check if token is valid
export const isTokenValid = (): boolean => {
  const token = localStorage.getItem('token')
  if (!token) return false

  const expiry = getTokenExpiry(token)
  if (!expiry) return false

  return new Date() < expiry
}

// Clear token
export const clearToken = (): void => {
  localStorage.removeItem('token')
}
